import {
  logAcabado,
  logAcabadoApi,
  logAcabadoResolve,
  logGola,
  logGolaResolve,
} from '@context/utils/linked/linkedAcabadoChangeLogger'
import cleanExistingSuffixes from '@context/utils/linked/cleanExistingSuffixes'
import {
  effectiveCatalogIdForAcabadoChange,
  resolveCatalogIdPreservingGolaForAcabadoChange,
} from '@context/utils/linked/buildLinkedCatalogId'
import getSuffixForAcabado from '@context/utils/linked/getSuffixForAcabado'
import { getGLBData } from '@proxies/getGLBData'

export type AcabadoSuffixKey = 'LA' | 'LI' | 'Ru' | 'R2'

export type LinkedIdCambioSource = {
  catalogID?: string | null
  idUnidades?: number | null
  ArmazonBlanco?: boolean | null
  idcambiogolablanco?: number | null
  idcambiogolapetroleo?: number | null
  idCambioGolaBlanco?: number | null
  idCambioGolaPetroleo?: number | null
  idCambioRutik?: number | null
  idCambioRutik2?: number | null
  idCambioLineal?: number | null
  idCambioLara?: number | null
}

function isSameUnidadId(item: LinkedIdCambioSource, id: number | null | undefined): boolean {
  if (id == null || item.idUnidades == null) return false
  return Number(id) === Number(item.idUnidades)
}

export type ParsedLinkedCatalog = {
  raw: string
  isPetroleo: boolean
  hasGola: boolean
  acabadoSuffix: AcabadoSuffixKey | null
}

export type AcabadoChangeDecision =
  | { strategy: 'id'; idUnidades: number }
  | { strategy: 'suffix'; catalogId: string }
  | { strategy: 'skip' }

export type GolaToggleDecision =
  | { strategy: 'id'; idUnidades: number }
  | { strategy: 'catalog'; catalogId: string }
  /** Misma unidad GLB: solo alternar golaActiva y tiradores (idCambioGola* === idUnidades) */
  | { strategy: 'modo'; golaActiva: boolean }
  | { strategy: 'skip' }

const ACABADO_SUFFIX_PATTERN = /\s+(LA|LI|Ru|R2)(?:\s+GO)?$/i

/** Código base sin PE ni sufijos LA/LI/Ru/R2/GO (p. ej. KAMB9060 LI → KAMB9060). */
export function normalizeLinkedCatalogBase(catalogID: string): string {
  let code = String(catalogID || '').trim().toUpperCase()
  if (code.startsWith('PE ')) code = code.slice(3).trim()
  return cleanExistingSuffixes(code).toUpperCase()
}

export function isKabvCatalog(catalogID: string): boolean {
  const base = normalizeLinkedCatalogBase(catalogID)
  return base.startsWith('KABV') || base.startsWith('KAPV')
}

export function supportsAcabadoVariantChange(item: LinkedIdCambioSource): boolean {
  return (
    item.idCambioLara != null ||
    item.idCambioLineal != null ||
    item.idCambioRutik != null ||
    item.idCambioRutik2 != null
  )
}

export function isAcabadoOnlyCatalogChange(fromCatalogID: string, toCatalogID: string): boolean {
  const from = String(fromCatalogID || '').trim().toUpperCase()
  const to = String(toCatalogID || '').trim().toUpperCase()
  if (!from || !to || from === to) return false
  return normalizeLinkedCatalogBase(from) === normalizeLinkedCatalogBase(to)
}

export function parseLinkedCatalog(catalogID: string): ParsedLinkedCatalog {
  const raw = String(catalogID || '').trim()
  const isPetroleo = raw.startsWith('PE ')
  const hasGola = /\sGO$/i.test(raw)
  const suffixMatch = raw.match(ACABADO_SUFFIX_PATTERN)
  const acabadoSuffix = normalizeAcabadoSuffixKey(suffixMatch?.[1])

  return { raw, isPetroleo, hasGola, acabadoSuffix }
}

export function normalizeAcabadoSuffixKey(value?: string | null): AcabadoSuffixKey | null {
  if (!value) return null
  const upper = value.toUpperCase()
  if (upper === 'LA') return 'LA'
  if (upper === 'LI') return 'LI'
  if (upper === 'RU') return 'Ru'
  if (upper === 'R2') return 'R2'
  return null
}

export function acabadoSuffixFromCatalog(catalogID: string): AcabadoSuffixKey | null {
  return parseLinkedCatalog(catalogID).acabadoSuffix
}

export function acabadoSuffixKeyFromAcabadoName(acabado: string): AcabadoSuffixKey | null {
  const suffix = getSuffixForAcabado(acabado).trim()
  return normalizeAcabadoSuffixKey(suffix)
}

export function suffixKeyToAcabadoLabel(suffix: AcabadoSuffixKey): string {
  switch (suffix) {
    case 'LA':
      return 'LARA'
    case 'LI':
      return 'LINEAL'
    case 'Ru':
      return 'RUSTIK'
    case 'R2':
      return 'RUSTIK2'
  }
}

export function resolveArmazonBlanco(item: LinkedIdCambioSource): boolean {
  const catalog = String(item.catalogID || '')
  if (catalog.startsWith('PE ')) return false
  if (typeof item.ArmazonBlanco === 'boolean') return item.ArmazonBlanco
  return true
}

export function resolveAcabadoChangeId(
  item: LinkedIdCambioSource,
  targetAcabado: string,
): number | null {
  const suffixKey = acabadoSuffixKeyFromAcabadoName(targetAcabado)
  if (!suffixKey) return null

  let id: number | null = null
  switch (suffixKey) {
    case 'LA':
      id = item.idCambioLara ?? null
      break
    case 'LI':
      id = item.idCambioLineal ?? null
      break
    case 'Ru':
      id = item.idCambioRutik ?? null
      break
    case 'R2':
      id = item.idCambioRutik2 ?? null
      break
    default:
      return null
  }

  if (isSameUnidadId(item, id)) {
    return null
  }

  return id
}

export function buildGolaOnCatalog(catalogID: string): string | null {
  const current = String(catalogID || '').trim()
  if (!current || /\sGO$/i.test(current)) return null
  const next = `${current} GO`
  return next !== current ? next : null
}

export function buildGolaOffCatalog(catalogID: string): string | null {
  const current = String(catalogID || '').trim()
  if (!/\sGO$/i.test(current)) return null
  const next = current.replace(/\s+GO$/i, '').trim()
  return next && next !== current ? next : null
}

export type LinkedGolaStateSource = LinkedIdCambioSource & {
  golaActiva?: boolean | null
}

/** idCambioGola* apunta a la misma unidad (ej. KCB260PER-2M): gola = ocultar tiradores, mismo GLB */
export function isSelfReferencingGolaUnit(item: LinkedIdCambioSource): boolean {
  const golaBlanco = item.idcambiogolablanco ?? item.idCambioGolaBlanco ?? null
  const golaPetroleo = item.idcambiogolapetroleo ?? item.idCambioGolaPetroleo ?? null
  const armazonBlanco = resolveArmazonBlanco(item)
  const golaId = armazonBlanco ? golaBlanco : (golaPetroleo ?? golaBlanco)
  return golaId != null && isSameUnidadId(item, golaId)
}

/** Gola real: sufijo GO en catalogID o marca golaActiva en store */
export function isGolaEffective(item: LinkedGolaStateSource): boolean {
  const catalog = String(item.catalogID || '')
  if (/\sGO$/i.test(catalog)) return true
  return item.golaActiva === true
}

export function shouldHideTiradoresForParent(
  parent: LinkedGolaStateSource,
  options?: {
    toolbarGolaActiva?: boolean | null
    sourceItem?: LinkedGolaStateSource | null
  },
): boolean {
  if (isGolaEffective(parent)) return true
  if (options?.sourceItem && isGolaEffective(options.sourceItem) && isSelfReferencingGolaUnit(parent)) {
    return true
  }
  if (options?.toolbarGolaActiva === true && isSelfReferencingGolaUnit(parent)) return true
  return false
}

/** Al dibujar: heredar modo gola booleano (sin sufijo GO) desde origen o toolbar */
export function shouldInheritGolaModoOnDraw(
  drawn: LinkedGolaStateSource,
  source: LinkedGolaStateSource | null | undefined,
  toolbarGolaActiva?: boolean | null,
): boolean {
  if (!isSelfReferencingGolaUnit(drawn)) return false
  if (isGolaEffective(drawn)) return false
  if (source && isGolaEffective(source)) return true
  if (toolbarGolaActiva === true) return true
  return false
}

/**
 * Toggle Gola (↔): idCambioGola* primero; si apunta a la misma idUnidades → modo booleano;
 * si no, fallback por catalogID ± GO.
 */
export function resolveGolaToggleTarget(item: LinkedGolaStateSource): GolaToggleDecision {
  const catalog = String(item.catalogID || '')
  const parsed = parseLinkedCatalog(catalog)
  const golaBlanco = item.idcambiogolablanco ?? item.idCambioGolaBlanco ?? null
  const golaPetroleo = item.idcambiogolapetroleo ?? item.idCambioGolaPetroleo ?? null
  const armazonBlanco = resolveArmazonBlanco(item)

  if (isSelfReferencingGolaUnit(item)) {
    const currentlyGola = isGolaEffective(item)
    const decision = { strategy: 'modo' as const, golaActiva: !currentlyGola }
    logGolaResolve(item, decision, { reason: 'idCambioGola igual a idUnidades — modo sin cambio GLB' })
    return decision
  }

  let resolvedId: number | null = null
  if (armazonBlanco && golaBlanco) resolvedId = golaBlanco
  else if (!armazonBlanco && golaPetroleo) resolvedId = golaPetroleo
  else resolvedId = golaBlanco ?? golaPetroleo ?? null

  const idValid = resolvedId != null && !isSameUnidadId(item, resolvedId)
  if (idValid) {
    const decision = { strategy: 'id' as const, idUnidades: resolvedId! }
    logGolaResolve(item, decision, {
      hasGola: parsed.hasGola,
      armazonBlanco,
      resolvedId,
    })
    return decision
  }

  const catalogId = parsed.hasGola ? buildGolaOffCatalog(catalog) : buildGolaOnCatalog(catalog)
  if (catalogId) {
    const decision = { strategy: 'catalog' as const, catalogId }
    logGolaResolve(item, decision, {
      hasGola: parsed.hasGola,
      armazonBlanco,
      reason: 'idCambioGola igual a idUnidades o ausente',
    })
    return decision
  }

  logGolaResolve(item, { strategy: 'skip' }, {
    hasGola: parsed.hasGola,
    idCambioGolaBlanco: golaBlanco,
    idCambioGolaPetroleo: golaPetroleo,
  })
  return { strategy: 'skip' }
}

/** @deprecated Usar resolveGolaToggleTarget */
export function resolveGolaToggleId(item: LinkedIdCambioSource): number | null {
  const decision = resolveGolaToggleTarget(item)
  return decision.strategy === 'id' ? decision.idUnidades : null
}

/**
 * Heredar Gola+acabado (LA GO, Ru GO, …) al dibujar vía catalogID.
 * Los idCambio* de acabado apuntan a la variante sin GO.
 */
export function resolveDrawInheritanceCatalog(
  drawnCatalogID: string,
  sourceCatalogID: string,
): string | null {
  const source = parseLinkedCatalog(sourceCatalogID)
  const drawn = parseLinkedCatalog(drawnCatalogID)
  if (!source.hasGola || drawn.hasGola) return null

  if (!source.acabadoSuffix) return null

  const baseWithoutSuffix = cleanExistingSuffixes(drawnCatalogID)
  const suffix = getSuffixForAcabado(suffixKeyToAcabadoLabel(source.acabadoSuffix))
  const withSuffix = `${baseWithoutSuffix}${suffix}`
  const next = `${withSuffix} GO`
  return next === drawnCatalogID ? null : next
}

/**
 * Heredar Gola al dibujar: idCambioGola* de la unidad dibujada (incluye LA GO, LI GO, …).
 * Fallback por catalogID solo si el id apunta a la misma unidad o no existe.
 */
export function resolveDrawGolaInheritanceTarget(
  drawnItem: LinkedIdCambioSource,
  sourceCatalogID: string,
  options?: { armazonBlanco?: boolean; sourceGolaEffective?: boolean },
): GolaToggleDecision {
  const source = parseLinkedCatalog(sourceCatalogID)
  const drawn = parseLinkedCatalog(String(drawnItem.catalogID || ''))
  const sourceHasGola = source.hasGola || options?.sourceGolaEffective === true
  if (!sourceHasGola || drawn.hasGola) return { strategy: 'skip' }

  const armazonBlanco = options?.armazonBlanco ?? resolveArmazonBlanco(drawnItem)
  const golaBlanco = drawnItem.idcambiogolablanco ?? drawnItem.idCambioGolaBlanco ?? null
  const golaPetroleo = drawnItem.idcambiogolapetroleo ?? drawnItem.idCambioGolaPetroleo ?? null

  let resolvedId: number | null = null
  if (armazonBlanco && golaBlanco) resolvedId = golaBlanco
  else if (!armazonBlanco && golaPetroleo) resolvedId = golaPetroleo
  else resolvedId = golaBlanco ?? golaPetroleo ?? null

  if (resolvedId != null && !isSameUnidadId(drawnItem, resolvedId)) {
    return { strategy: 'id', idUnidades: resolvedId }
  }

  const drawnCatalog = String(drawnItem.catalogID || '')
  let catalogId: string | null = null

  if (source.acabadoSuffix) {
    const base = cleanExistingSuffixes(drawnCatalog)
    const suffix = getSuffixForAcabado(suffixKeyToAcabadoLabel(source.acabadoSuffix))
    const withSuffix = drawn.acabadoSuffix ? drawnCatalog : `${base}${suffix}`
    catalogId = buildGolaOnCatalog(withSuffix)
  } else {
    catalogId = buildGolaOnCatalog(drawnCatalog)
  }

  if (catalogId && catalogId !== drawnCatalog) {
    return { strategy: 'catalog', catalogId }
  }

  return { strategy: 'skip' }
}

/** @deprecated Usar resolveDrawGolaInheritanceTarget */
export function resolveDrawGolaInheritanceId(
  drawnItem: LinkedIdCambioSource,
  sourceCatalogID: string,
  options?: { armazonBlanco?: boolean },
): number | null {
  const decision = resolveDrawGolaInheritanceTarget(drawnItem, sourceCatalogID, options)
  return decision.strategy === 'id' ? decision.idUnidades : null
}

export function buildCatalogWithAcabadoSuffix(
  catalogID: string,
  targetAcabado: string,
): string | null {
  const current = String(catalogID || '')
  const suffix = getSuffixForAcabado(targetAcabado)
  const parsed = parseLinkedCatalog(current)

  if (parsed.hasGola) {
    const removeSuffixBeforeGO = (code: string) => code.replace(/\s+(LA|LI|Ru|R2)\s+GO$/i, ' GO')
    const currentNoSpecial = removeSuffixBeforeGO(current)
    const base = currentNoSpecial.replace(/\s+GO$/i, '')
    const trimmedSuffix = suffix.trim()

    if (!trimmedSuffix) {
      return `${base} GO`
    }
    return `${base} ${trimmedSuffix} GO`
  }

  const baseCatalogId = cleanExistingSuffixes(current)
  const next = `${baseCatalogId}${suffix}`
  return next === current ? null : next
}

export function resolveAcabadoChangeTarget(
  item: LinkedIdCambioSource & { golaActiva?: boolean | null },
  targetAcabado: string,
): AcabadoChangeDecision {
  const currentCatalog = effectiveCatalogIdForAcabadoChange(item)

  if (isKabvCatalog(currentCatalog) && !supportsAcabadoVariantChange(item)) {
    logAcabadoResolve(item, targetAcabado, { strategy: 'skip' }, null)
    return { strategy: 'skip' }
  }

  const idUnidades = resolveAcabadoChangeId(item, targetAcabado)

  if (idUnidades) {
    const decision = { strategy: 'id' as const, idUnidades }
    logAcabadoResolve(item, targetAcabado, decision)
    return decision
  }

  const catalogId = buildCatalogWithAcabadoSuffix(currentCatalog, targetAcabado)
  if (catalogId && catalogId !== currentCatalog) {
    const decision = { strategy: 'suffix' as const, catalogId }
    logAcabadoResolve(item, targetAcabado, decision)
    return decision
  }

  logAcabadoResolve(item, targetAcabado, { strategy: 'skip' })
  return { strategy: 'skip' }
}

export async function fetchUnidadById(idUnidades: number): Promise<Record<string, unknown> | null> {
  const inFlight = unidadByIdInFlight.get(idUnidades)
  if (inFlight) return inFlight

  const request = (async () => {
    logAcabado('FETCH_UNIDAD_BY_ID', { idUnidades })
    try {
      const data = await getGLBData(idUnidades)
      logAcabadoApi('getGLBData(idUnidades)', { idUnidades }, data)
      if (!data) return null
      return data as unknown as Record<string, unknown>
    } catch (error) {
      logAcabado('FETCH_UNIDAD_BY_ID_ERROR', { idUnidades, error: String(error) })
      return null
    } finally {
      unidadByIdInFlight.delete(idUnidades)
    }
  })()

  unidadByIdInFlight.set(idUnidades, request)
  return request
}

const unidadByIdInFlight = new Map<number, Promise<Record<string, unknown> | null>>()

export type AcabadoTargetResolveInput = {
  itemId: string
  item: LinkedIdCambioSource
}

/** Resuelve catalogIDs destino para N ítems en paralelo (deduplica fetch por idUnidades). */
export async function resolveAcabadoTargetsForItems(
  inputs: AcabadoTargetResolveInput[],
  targetAcabado: string,
): Promise<Map<string, string | null>> {
  const result = new Map<string, string | null>()
  const idFetchCache = new Map<number, Promise<Record<string, unknown> | null>>()

  await Promise.all(
    inputs.map(async ({ itemId, item }) => {
      const decision = resolveAcabadoChangeTarget(item, targetAcabado)
      if (decision.strategy === 'skip') {
        result.set(itemId, null)
        return
      }
      if (decision.strategy === 'suffix') {
        logAcabadoResolve(item, targetAcabado, decision, decision.catalogId)
        result.set(itemId, decision.catalogId)
        return
      }

      let fetchPromise = idFetchCache.get(decision.idUnidades)
      if (!fetchPromise) {
        fetchPromise = fetchUnidadById(decision.idUnidades)
        idFetchCache.set(decision.idUnidades, fetchPromise)
      }
      const unidad = await fetchPromise
      const codigo = unidad?.codigo
      const effectiveCatalog = effectiveCatalogIdForAcabadoChange(item)
      const resolved =
        typeof codigo === 'string' && codigo.length > 0
          ? resolveCatalogIdPreservingGolaForAcabadoChange(effectiveCatalog, codigo)
          : null
      logAcabadoResolve(item, targetAcabado, decision, resolved)
      result.set(itemId, resolved)
    }),
  )

  return result
}

export async function fetchUnidadByCatalog(
  catalogId: string,
): Promise<Record<string, unknown> | null> {
  logAcabado('FETCH_UNIDAD_BY_CATALOG', { catalogId })
  try {
    const data = await getGLBData(catalogId)
    logAcabadoApi('getGLBData(catalogId)', { catalogId }, data)
    if (!data) return null
    return data as unknown as Record<string, unknown>
  } catch (error) {
    logAcabado('FETCH_UNIDAD_BY_CATALOG_ERROR', { catalogId, error: String(error) })
    return null
  }
}

export async function resolveAcabadoTargetCatalogId(
  item: LinkedIdCambioSource,
  targetAcabado: string,
): Promise<string | null> {
  const decision = resolveAcabadoChangeTarget(item, targetAcabado)
  if (decision.strategy === 'skip') {
    logAcabadoResolve(item, targetAcabado, decision, null)
    return null
  }

  if (decision.strategy === 'id') {
    const unidad = await fetchUnidadById(decision.idUnidades)
    const codigo = unidad?.codigo
    const effectiveCatalog = effectiveCatalogIdForAcabadoChange(item)
    const resolved =
      typeof codigo === 'string' && codigo.length > 0
        ? resolveCatalogIdPreservingGolaForAcabadoChange(effectiveCatalog, codigo)
        : null
    logAcabadoResolve(item, targetAcabado, decision, resolved)
    return resolved
  }

  logAcabadoResolve(item, targetAcabado, decision, decision.catalogId)
  return decision.catalogId
}

const ID_CAMBIO_FIELD_MAP: Array<[apiKey: string, itemKey: string]> = [
  ['idCambioArmazon', 'idcambioarmazon'],
  ['idCambioGolaBlanco', 'idcambiogolablanco'],
  ['idCambioGolaPetroleo', 'idcambiogolapetroleo'],
  ['idCambioRutik', 'idCambioRutik'],
  ['idCambioRutik2', 'idCambioRutik2'],
  ['idCambioLineal', 'idCambioLineal'],
  ['idCambioLara', 'idCambioLara'],
]

export function applyIdCambioFromApi<T extends Record<string, unknown>>(
  item: T,
  apiData: Record<string, unknown>,
): void {
  for (const [apiKey, itemKey] of ID_CAMBIO_FIELD_MAP) {
    if (Object.prototype.hasOwnProperty.call(apiData, apiKey)) {
      ;(item as Record<string, unknown>)[itemKey] = apiData[apiKey] ?? null
    }
  }
}
